import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useReactToPrint } from "react-to-print";
import ReactPaginate from "react-paginate";
import MobileNav from "../mobileNav/MobileNav";
import "./rejectedList.css";
import printIcon from "../../assets/print-icon.svg";
import PrintingHeader from "../printingHeader/PrintingHeader";

const RejectedList = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [warrantyDetailsPopup, setWarrantyDetailsPopup] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const maxLength = 10;

  const itemsPerPageOptions = [25, 50, 100, 300, 400, totalItems];

  const getAllAppointments = async (
    currentPage = 1,
    pageSize = itemsPerPage
  ) => {
    try {
      setLoading(true);
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);
      const url = `https://api.generalhome.sa/api/maintenance/GetRejectedRequests`;

      const payload = {
        maintenanceAreaIds: maintenanceAreaIds,
        Page: currentPage,
        PageSize: pageSize,
      };

      const response = await axios.post(url, payload);

      // Assuming the API response structure is similar to GetFinishedRequests
      const totalItems = response.data.TotalItems || response.data.length;
      const totalPages = response.data.TotalPages || 1;
      const totalNumberOfPages = totalPages || Math.ceil(totalItems / pageSize);

      setPageCount(totalNumberOfPages);
      setData(response.data.Items || response.data);
      setTotalItems(totalItems);
      setCurrentPage(currentPage - 1);
    } catch (err) {
      setError(err.message);
      setData([]);
      setTotalItems(0);
      setPageCount(0);
      setCurrentPage(0);
    } finally {
      setLoading(false);
    }
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
    onAfterPrint: () => console.log("print success"),
  });

  const handleShowWarrantyDetails = (maintenanceDetails) => {
    setWarrantyDetailsPopup(maintenanceDetails);
  };

  const handleCloseWarrantyDetails = () => {
    setWarrantyDetailsPopup(null);
  };

  // Pagination click handler
  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    const pageNumber = selectedPage + 1;
    getAllAppointments(pageNumber, itemsPerPage);
  };

  // Items per page change handler
  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
    getAllAppointments(1, newItemsPerPage);
  };

  const formatACTypes = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";
    return maintenanceDetails.map((detail) => detail.ACTypeName).join("، ");
  };

  const getWarrantyStatus = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";

    const firstStatus = maintenanceDetails[0].WarrantyStatusName;
    const allSame = maintenanceDetails.every(
      (item) => item.WarrantyStatusName === firstStatus
    );

    return allSame ? firstStatus : "مختلط";
  };

  // Render pagination component
  const renderPagination = () => (
    <ReactPaginate
      forcePage={currentPage}
      previousLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.94 4L6 4.94L9.05333 8L6 11.06L6.94 12L10.94 8L6.94 4Z"
            fill="black"
          />
        </svg>
      }
      nextLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.06 12L11 11.06L7.94667 8L11 4.94L10.06 4L6.06 8L10.06 12Z"
            fill="black"
            fillOpacity="0.3"
          />
        </svg>
      }
      breakLabel={"..."}
      pageCount={pageCount}
      marginPagesDisplayed={2}
      pageRangeDisplayed={3}
      onPageChange={handlePageClick}
      containerClassName={"pagination justify-content-end"}
      pageClassName={"page-item"}
      pageLinkClassName={"page-link"}
      previousClassName={"page-item"}
      previousLinkClassName={"page-link"}
      nextClassName={"page-item"}
      nextLinkClassName={"page-link"}
      breakClassName={"page-item"}
      breakLinkClassName={"break-link"}
      activeClassName={"active"}
    />
  );

  useEffect(() => {
    getAllAppointments(1, itemsPerPage);
  }, []);

  return (
    <>
      <MobileNav />
      <section className="newAppointment rejectedList mt-0">
        <Helmet>
          <meta charSet="utf-8" />
          <title>الطلبات المرفوضه</title>
          <link
            rel="canonical"
            href="https://generalhouse.sa/dashboard/rejectedList"
          />
        </Helmet>
        {loading ? (
          <div className="spinner-container">
            <ClipLoader
              className="loader"
              loading={loading}
              size={100}
              aria-label="Loading Spinner"
              data-testid="loader"
              color="#00b1ff"
            />
          </div>
        ) : (
          <div className="newAppointment-container" ref={componentRef}>
            <PrintingHeader title="الطلبات المرفوضه" count={totalItems} />

            <div className="d-flex justify-content-between info">
              <div className="info-header">
                <h2>طلبات الصيانه المرفوضه</h2>
                <h3>{totalItems}</h3>
              </div>
              <div className="print-icon-holder">
                <img
                  src={printIcon}
                  alt="printIcon"
                  className="print-icon"
                  onClick={handlePrint}
                />
              </div>
            </div>

            {/* Items per page selector and pagination */}
            <div className="paginateComponent">
              <div className="items-per-page-selector">
                <select
                  id="itemsPerPage"
                  className="form-select form-select-sm"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  style={{
                    width: "80px",
                    display: "inline-block",
                    margin: "0 10px",
                  }}
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {pageCount > 0 && renderPagination()}
            </div>

            <div className="table-responsive-sm overflow-auto">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th scope="row">الإسم</th>
                    <th scope="row">رقم الهاتف</th>
                    <th scope="row">المنطقه</th>
                    <th scope="row">المدينه</th>
                    <th scope="row">الحي</th>
                    <th scope="row">نوع المكيف</th>
                    <th scope="row">حالة الضمان</th>
                    <th scope="row" className="text-center">
                      التحكم
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 ? (
                    data.map((item) => {
                      const warrantyStatus = getWarrantyStatus(
                        item.MaintenanceRequestDetails
                      );
                      return (
                        <tr key={item.RequestId}>
                          <td
                            style={{
                              position: "relative",
                              padding: 0,
                              borderTopRightRadius: "8px",
                              borderBottomRightRadius: "8px",
                            }}
                          >
                            <div
                              style={
                                item.MaintenanceTypeId === 2
                                  ? {
                                      position: "absolute",
                                      top: 0,
                                      right: 0,
                                      bottom: 0,
                                      left: 0,
                                      borderTopRightRadius: "8px",
                                      borderBottomRightRadius: "8px",
                                      borderRight: "4px solid #007bff",
                                      padding: "8px",
                                      display: "flex",
                                    }
                                  : {
                                      padding: "8px",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                    }
                              }
                            >
                              {item.CustomerName &&
                              item.CustomerName.length > maxLength
                                ? `${item.CustomerName.substring(
                                    0,
                                    maxLength
                                  )}...`
                                : item.CustomerName}
                            </div>
                          </td>
                          <td>
                            <a
                              href={`https://api.whatsapp.com/send?phone=+966${item.PhoneNumber.replace(
                                /[^\d+]/g,
                                ""
                              )}&text=Hello,%20I'm%20interested%20in%20your%20product`}
                              target="__blank"
                              rel="noopener noreferrer"
                            >
                              {item.PhoneNumber}
                            </a>
                          </td>
                          <td>
                            {item.Region && item.Region.length > maxLength
                              ? `${item.Region.substring(0, maxLength)}...`
                              : item.Region}
                          </td>
                          <td>
                            {item.City && item.City.length > maxLength
                              ? `${item.City.substring(0, maxLength)}...`
                              : item.City}
                          </td>
                          <td>
                            {item.Neighborhood &&
                            item.Neighborhood.length > maxLength
                              ? `${item.Neighborhood.substring(
                                  0,
                                  maxLength
                                )}...`
                              : item.Neighborhood}
                          </td>
                          <td>
                            {formatACTypes(item.MaintenanceRequestDetails)
                              ?.length > maxLength
                              ? `${formatACTypes(
                                  item.MaintenanceRequestDetails
                                ).substring(0, maxLength)}...`
                              : formatACTypes(item.MaintenanceRequestDetails)}
                          </td>
                          <td
                            className={
                              warrantyStatus === "داخل الضمان"
                                ? "warranty-inside"
                                : "warranty-outside"
                            }
                          >
                            {warrantyStatus === "مختلط" ? (
                              <span
                                className="mixed-warranty"
                                onClick={() =>
                                  handleShowWarrantyDetails(
                                    item.MaintenanceRequestDetails
                                  )
                                }
                                style={{ cursor: "pointer" }}
                              >
                                مختلط
                              </span>
                            ) : (
                              <p>{warrantyStatus}</p>
                            )}
                          </td>
                          <td className="text-center btn-holder">
                            <Link
                              to={`/dashboard/inProgress/${item.RequestId}`}
                              className="link m-1"
                            >
                              <button>تفاصيل</button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="no-data-message">
                      <td colSpan="8">لا توجد بيانات لعرضها حاليا :(</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {warrantyDetailsPopup && (
          <div className="warranty-details-popup">
            <div className="warranty-details-card">
              <div className="warranty-details-header">
                <h3>تفاصيل حالة الضمان</h3>
                <button
                  onClick={handleCloseWarrantyDetails}
                  className="close-btn"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="warranty-details-body">
                <div className="warranty-status-summary">
                  <span className="summary-label">الحالة الإجمالية:</span>
                  <span
                    className={`summary-value ${
                      getWarrantyStatus(warrantyDetailsPopup) === "داخل الضمان"
                        ? "inside"
                        : "outside"
                    }`}
                  >
                    {getWarrantyStatus(warrantyDetailsPopup)}
                  </span>
                </div>
                <div className="warranty-items-container">
                  {warrantyDetailsPopup.map((item, index) => (
                    <div key={index} className="warranty-item">
                      <div className="ac-type">
                        <i className="fas fa-snowflake"></i>
                        <span>{item.ACTypeName}</span>
                      </div>
                      <div
                        className={`warranty-status ${
                          item.WarrantyStatusName === "داخل الضمان"
                            ? "inside"
                            : "outside"
                        }`}
                      >
                        {item.WarrantyStatusName}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="warranty-details-footer">
                <button
                  onClick={handleCloseWarrantyDetails}
                  className="close-button"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default RejectedList;
